import { useCallback, useLayoutEffect, useRef } from "react";

/**
 * Keeps a textarea exactly as tall as what is written in it.
 *
 * A note is READ far more often than it is written, and a fixed two-row box hid all but the first
 * two lines of a five-line one behind a scrollbar — so the trainer could not check what the
 * trainee will be shown without scrolling inside a box the size of a stamp. Growing the field
 * instead means the card is always showing the whole note.
 *
 * <p>Measured rather than counted: `\n`s are not lines. A single long sentence wraps to three rows
 * in a narrow window and one in a wide one, and only the browser knows which. Resetting the height
 * to `auto` before reading `scrollHeight` is what lets the box SHRINK again — without it,
 * `scrollHeight` never reports less than the height already set.
 *
 * <p>Returns a callback ref: the node arrives on mount and on every re-mount, which a `useRef` +
 * `useEffect([])` pair silently misses (see `useStickyHeaderHeight` for the same reasoning).
 */
export const useAutoGrowTextarea = (value: string): ((node: HTMLTextAreaElement | null) => void) => {
  const node = useRef<HTMLTextAreaElement | null>(null);

  const fit = useCallback(() => {
    const el = node.current;
    if (!el) return;
    el.style.height = "auto";
    // jsdom lays nothing out, so scrollHeight is 0 there — leaving the CSS height alone keeps a
    // test rendering the box at its declared min-height instead of collapsing it to nothing.
    if (el.scrollHeight > 0) el.style.height = `${el.scrollHeight}px`;
  }, []);

  const ref = useCallback(
    (el: HTMLTextAreaElement | null) => {
      node.current = el;
      fit();
    },
    [fit]
  );

  // every keystroke arrives as a new `value`, and the window can be resized under a written note
  useLayoutEffect(() => {
    fit();
    window.addEventListener("resize", fit);
    return () => window.removeEventListener("resize", fit);
  }, [value, fit]);

  return ref;
};

export default useAutoGrowTextarea;
